import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum, SectionEnum, WorkOrderActKc2Data, WorkOrderObjectResultData } from 'gql/graphql';
import { Loader, Button, IconRestart, Select } from 'uikit';
import { descriptionNumberAndDate } from 'utils/descriptionNumberAndDate';

import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { gqlWorkOrderObjectResult } from '../workOrderQueries';

import { WorkOrderSummaryItem } from './WorkOrderSummaryItem';

import Styles from './WorkOrderSummary.module.css';

export interface IWorkOrderSummarySelectItem {
    id: string;
    label: string;
}

interface IWorkOrderSummaryProps {
    chapter?: ChapterEnum | null;
    section?: SectionEnum;
    workOrderObjectId: number;
    isVisible: boolean;
    workOrderPriceListData: WorkOrderActKc2Data[];
    costRounding: number;
}

export const chapterWorkOrderSummarySelectItem = { id: 'chapter', label: 'Итоги Главы' };

export const WorkOrderSummary: React.FC<IWorkOrderSummaryProps> = (props) => {
    const { chapter, section, workOrderObjectId, isVisible, workOrderPriceListData, costRounding } = props;
    const dispatch = useDispatch();

    const summaryItems = useMemo<IWorkOrderSummarySelectItem[]>(
        () =>
            (workOrderPriceListData ?? []).map((act) => ({
                id: act.id,
                label: `Итоги Акта${descriptionNumberAndDate(
                    act.isCorrection ? act.fullNumber : act.number,
                    act.isCorrection ? act.correctionDate : act.date,
                )}`,
            })),
        [workOrderPriceListData],
    );

    const [workOrderObjectResultData, workOrderObjectResultStatus, , workOrderObjectResultMakeRequest] = useRequest({
        requestId: 'workOrderObjectResult',
        query: gqlWorkOrderObjectResult,
        variables: { workOrderObjectId, chapter, section },
        withEffect: false,
    });

    const isLoading = workOrderObjectResultStatus === RequestStatus.FETCHING;

    const [selectedSummaryItem, setSelectedSummaryItem] = useState<IWorkOrderSummarySelectItem | null>(
        chapterWorkOrderSummarySelectItem,
    );

    const onDataRefreshRequest = useCallback(() => {
        const vars = { workOrderObjectId, chapter, section };

        dispatch(workOrderObjectResultMakeRequest(vars));
    }, [chapter, dispatch, section, workOrderObjectId, workOrderObjectResultMakeRequest]);

    const onSelectedSummaryItemChange = useCallback(
        (selectedItem) => {
            if (selectedItem !== selectedSummaryItem) {
                setSelectedSummaryItem(selectedItem);
            }
        },
        [selectedSummaryItem],
    );

    useEffect(() => {
        if (isVisible) {
            onDataRefreshRequest();
        }
    }, [isVisible, onDataRefreshRequest]);

    const actualData = useMemo(
        () =>
            workOrderObjectResultData?.uerWorkOrderObjectResult?.filter((item) =>
                selectedSummaryItem?.id === chapterWorkOrderSummarySelectItem.id
                    ? item.actKC2Id === null
                    : item.actKC2Id === Number(selectedSummaryItem?.id),
            ),
        [workOrderObjectResultData?.uerWorkOrderObjectResult, selectedSummaryItem],
    );

    const items = useMemo(() => [chapterWorkOrderSummarySelectItem, ...summaryItems], [summaryItems]);

    const onChange = useCallback(
        ({ value }) => {
            onSelectedSummaryItemChange(value);
        },
        [onSelectedSummaryItemChange],
    );

    return (
        <div className={Styles.workOrderSummaryContainer}>
            <div className={Styles.workOrderSummaryHeader}>
                <Select
                    size="s"
                    items={items}
                    value={selectedSummaryItem}
                    onChange={onChange}
                    className={Styles.workOrderSummaryHeaderCombo}
                />
                <Button
                    iconLeft={IconRestart}
                    disabled={isLoading}
                    view="secondary"
                    onClick={onDataRefreshRequest}
                    size="s"
                />
            </div>
            <div className={Styles.workOrderSummaryContent}>
                {isLoading && <Loader className={Styles.workOrderSummaryLoader} />}
                {!isLoading &&
                    actualData?.map((item, index) => (
                        <WorkOrderSummaryItem
                            item={item as WorkOrderObjectResultData}
                            costRounding={costRounding}
                            key={index}
                        />
                    ))}
            </div>
        </div>
    );
};

WorkOrderSummary.displayName = 'WorkOrderSummary';
